import { ScoresApi, LEADERBOARD_GEEKS_LIMIT } from '@/api';
import { GeekItemForLeaderboard, Skill } from '@/models';
import { GeekProfileGeneralPosition } from '@/models/dashboard';
import { GeekProfileGeneralScore, GeekSkill } from '@/models/geek-profile';
import { LeaderboardFilters, PersonalBest } from '@/models/leaderboard';
import { Module } from 'vuex';
import { Actions } from './actions';
import { RootState } from './root.store';
import { Mutations } from './mutations';

export interface ScoresState {
  generalPosition: GeekProfileGeneralPosition;
  generalScore: GeekProfileGeneralScore;
  skillScores: GeekSkill[];
  leaderboard: GeekItemForLeaderboard[];
  personalBest: PersonalBest;
  pagination: {
    offset: number,
    totalCount: number,
  };
}

const scoresApi = new ScoresApi();

export const ScoresModule: Module<ScoresState, RootState> = {
  state: {
    generalPosition: {
      level: 0,
      leaderboardPosition: 0,
      gxpToJumpOnNextPosition: 0,
      nextPosition: 0,
      isClassified: true,
    },
    generalScore: {
      level: 0,
      gxpCollectedForNextLevel: 0,
      gxpStepForNextLevel: 0,
      leaderboardPosition: 0,
      isClassified: true,
      totalGxp: 0,
    },
    skillScores: [] as GeekSkill[],
    leaderboard: [] as GeekItemForLeaderboard[],
    personalBest: { skill: Skill.General },
    pagination: {
      offset: 0,
      totalCount: 0,
    },
  },

  mutations: {
    [Mutations.scores.setGeekGeneralScore](state, generalScore: GeekProfileGeneralScore) {
      state.generalScore = generalScore;
    },
    [Mutations.scores.setGeekGeneralPosition](state, generalPosition: GeekProfileGeneralPosition) {
      state.generalPosition = generalPosition;
    },
    [Mutations.scores.setGeekSkillScores](state, skillScores: GeekSkill[]) {
      state.skillScores = skillScores;
    },
    [Mutations.scores.setGeekPersonalBest](state, personalBest: PersonalBest) {
      state.personalBest = personalBest;
    },
    [Mutations.scores.setLeaderboard](state, geeks: GeekItemForLeaderboard[]) {
      state.leaderboard = geeks;
    },
    [Mutations.scores.setLeaderboardGeeksCount](state, count: number) {
      state.pagination.totalCount = count;
    },
    [Mutations.scores.resetPagination](state) {
      state.pagination.offset = 0;
      state.pagination.totalCount = 0;
    },
    [Mutations.scores.addLeaderboardGeeks](state, geeks: GeekItemForLeaderboard[]) {
      state.leaderboard.push(...geeks);
    },
    [Mutations.scores.incrementPagination](state) {
      state.pagination.offset += LEADERBOARD_GEEKS_LIMIT;
    },
  },

  actions: {
    async [Actions.scores.fetchGeekGeneralScore]({ commit }, { seasonId, geekId }): Promise<void> {
      const geekGeneralScore = await scoresApi.fetchGeekGeneralScore(seasonId, geekId);
      commit(Mutations.scores.setGeekGeneralScore, geekGeneralScore);
    },
    async [Actions.scores.fetchGeekGeneralPosition]({ commit }, geekId?: string): Promise<void> {
      const geekGeneralPosition = await scoresApi.fetchGeekGeneralPosition(geekId);
      commit(Mutations.scores.setGeekGeneralPosition, geekGeneralPosition);
    },
    async [Actions.scores.fetchGeekSkillScores]({ commit }, { seasonId, geekId }): Promise<void> {
      const geekSkillScores = await scoresApi.fetchGeekSkillScores(seasonId, geekId);
      commit(Mutations.scores.setGeekSkillScores, geekSkillScores);
    },
    async [Actions.scores.fetchGeekPersonalBest]({ commit }, { seasonId, geekId }): Promise<void> {
      const geekPersonalBest = await scoresApi.fetchGeekPersonalBest(seasonId, geekId);
      commit(Mutations.scores.setGeekPersonalBest, geekPersonalBest);
    },
    async [Actions.scores.fetchLeaderboard]({ commit, state }, filters: LeaderboardFilters): Promise<void> {
      commit(Mutations.scores.resetPagination);
      const geeks = await scoresApi.getLeaderboard(filters, state.pagination.offset);
      commit(Mutations.scores.setLeaderboard, geeks.data);
      commit(Mutations.scores.setLeaderboardGeeksCount, geeks.totalCount);
    },
    async [Actions.scores.fetchMoreLeaderboard]({ commit, state }, filters: LeaderboardFilters): Promise<void> {
      commit(Mutations.scores.incrementPagination);
      const geeks = await scoresApi.getLeaderboard(filters, state.pagination.offset);
      commit(Mutations.scores.addLeaderboardGeeks, geeks.data);
      commit(Mutations.scores.setLeaderboardGeeksCount, geeks.totalCount);
    },
    [Actions.scores.resetGeekGeneralScore]({ commit }) {
      commit(Mutations.scores.setGeekGeneralScore, {});
    },
    [Actions.scores.resetGeekSkillScores]({ commit }) {
      commit(Mutations.scores.setGeekSkillScores, []);
    },
  },
};
